/**
 * Driver Career Projection — utility helpers for the HR "Plan de carrière" mini-tool.
 *
 * Two pure functions :
 *   1. projectLevelUp(driver, contextActiveDays) — returns the estimated number of
 *      days the driver will need to reach his next level, given his current
 *      working pattern (mission frequency).
 *   2. suggestSpecialties(driver, activeContracts) — ranks specialty trainings
 *      the driver has NOT yet acquired by their current commercial demand
 *      (how many active contracts of that family are running), so the player
 *      knows which formation pays off most right now.
 *
 * Both functions are deterministic, side-effect free, JS-only — no React,
 * no Zustand. Perfect for unit testing.
 */
import { getDriverSpecialties } from './driverSpecialties';

// Same XP thresholds the HR slice uses for level-ups.
const XP_THRESHOLDS = [0, 100, 300, 600, 1000, 1500];
const getXpForLevel = (level) =>
  XP_THRESHOLDS[level] || XP_THRESHOLDS[XP_THRESHOLDS.length - 1] + (level - XP_THRESHOLDS.length + 1) * 600;

/**
 * Project the driver's days-to-next-level given his current activity pattern.
 *
 * The HR slice's `addDailyXp` grants:
 *   - +3 XP/day when idle / resting
 *   - +6 XP/day when active (assigned to a vehicle, no mission)
 *   - +12 XP/day when on mission (vehicle has assignedContractId)
 *
 * Without future-state simulation, we use the driver's CURRENT status as
 * the daily XP rate baseline. The result is rounded UP to integer days.
 *
 * Returns:
 *   {
 *     currentLevel,
 *     currentXp,
 *     nextLevel,                // null if at max projection bracket
 *     nextThreshold,            // cumulative XP needed to reach nextLevel
 *     remainingXp,              // XP still to earn
 *     dailyXp,                  // current XP per day
 *     daysToNextLevel,          // ceil(remainingXp / dailyXp), 0 if already eligible
 *     activityLabel,            // "à l'arrêt" / "actif" / "en mission"
 *     ready,                    // currentXp >= nextThreshold (eligible right now)
 *   }
 */
export function projectLevelUp(driver) {
  if (!driver) return null;
  const currentLevel = driver.level || 1;
  const currentXp = driver.xp || 0;
  const nextLevel = currentLevel + 1;
  const nextThreshold = getXpForLevel(nextLevel);
  const remainingXp = Math.max(0, nextThreshold - currentXp);

  // Determine daily XP from status. (status='active' covers both idle-assigned
  // and on-mission in the HR slice — we use the heuristic: if the driver has
  // a current mission flag, it's mission; else if assigned to a vehicle, active;
  // else idle.)
  let dailyXp = 3;
  let activityLabel = "à l'arrêt";
  if (driver.status === 'active') {
    // We can't know from the driver alone if a contract is currently running
    // — caller passes that hint via driver._currentlyOnMission if available.
    if (driver._currentlyOnMission) {
      dailyXp = 12;
      activityLabel = 'en mission';
    } else {
      dailyXp = 6;
      activityLabel = 'actif sans mission';
    }
  } else if (driver.status === 'training') {
    dailyXp = 0;
    activityLabel = 'en formation';
  } else if (driver.status === 'vacation' || driver.status === 'sick') {
    dailyXp = 0;
    activityLabel = driver.status === 'sick' ? 'arrêt maladie' : 'en congés';
  }

  const daysToNextLevel = dailyXp > 0 ? Math.ceil(remainingXp / dailyXp) : null;
  return {
    currentLevel,
    currentXp,
    nextLevel,
    nextThreshold,
    remainingXp,
    dailyXp,
    daysToNextLevel,
    activityLabel,
    ready: currentXp >= nextThreshold,
  };
}

/**
 * Suggest the most profitable specialty trainings the driver does NOT yet
 * possess, ranked by current commercial demand (count of active contracts
 * in that category).
 *
 * @param {object} driver
 * @param {Array<{category:string, requiresSpecialty?:string}>} activeContracts
 * @param {Array<{id:string, name:string, spec:string, cost:number, days:number}>} availableSpecTrainings
 *   subset of TRAININGS (group='spec') exposed by HR.jsx — kept as a param
 *   to avoid a circular import.
 * @returns {Array<{trainingId, name, spec, cost, days, demandCount, demandContracts}>}
 *   ordered most-demanded first. Empty array if no contract maps to a known
 *   specialty the driver lacks.
 */
export function suggestSpecialties(driver, activeContracts, availableSpecTrainings) {
  if (!driver || !Array.isArray(activeContracts) || !Array.isArray(availableSpecTrainings)) return [];
  const owned = new Set(getDriverSpecialties(driver));

  // Map contract category → list of viable specialty IDs (same aliases as
  // the contract gate logic).
  const CATEGORY_TO_SPECS = {
    'Messagerie / Colis':   ['Messagerie'],
    'Frigorifique':         ['Frigorifique'],
    'BTP / Matériaux':      ['BTP'],
    'Vrac / Palettes':      ['Vrac'],
    'Export EU':            ['Export EU'],
    'Spécialisé':           ['ADR', 'BTP', 'Frigorifique', 'Citerne', 'Spécialisé'],
  };

  // Count demand per specialty.
  const demandBySpec = {};
  for (const c of activeContracts) {
    const cats = CATEGORY_TO_SPECS[c.category] || [];
    for (const spec of cats) {
      if (!demandBySpec[spec]) demandBySpec[spec] = { count: 0, contracts: [] };
      demandBySpec[spec].count += 1;
      demandBySpec[spec].contracts.push(c.name || c.category);
    }
    // Also include explicit requiresSpecialty.
    if (c.requiresSpecialty) {
      const s = c.requiresSpecialty;
      if (!demandBySpec[s]) demandBySpec[s] = { count: 0, contracts: [] };
      demandBySpec[s].count += 1;
      demandBySpec[s].contracts.push(c.name || c.category);
    }
  }

  // Map back to specialty trainings the driver doesn't yet have.
  return availableSpecTrainings
    .filter(tr => tr.spec && !owned.has(tr.spec))
    .map(tr => {
      const demand = demandBySpec[tr.spec] || { count: 0, contracts: [] };
      return {
        trainingId: tr.id,
        name: tr.name,
        spec: tr.spec,
        cost: tr.cost,
        days: tr.days,
        demandCount: demand.count,
        demandContracts: [...new Set(demand.contracts)].slice(0, 3),
      };
    })
    .sort((a, b) => b.demandCount - a.demandCount);
}
